import {
  type Variance,
  compareToBaseline,
} from '@pst/core-scheduling';
import type { ActiveBaseline, PlanView, SchedTask } from '../../api';
import { calendarOf } from './localSchedule';

/**
 * Baseline comparison for the interface.
 *
 * Computed in the browser with the engine's own compareToBaseline, from the
 * schedule actually on screen. During a drag that is the preview, so the
 * variance column moves with the bar instead of catching up after the release.
 */
export interface BaselineDisplay {
  name: string;
  /** Frozen dates by task id, for drawing the planned bar. */
  planned: Map<string, { start: string; finish: string }>;
  variances: Map<string, Variance>;
}

export function baselineDisplay(
  plan: PlanView,
  tasks: readonly SchedTask[],
  baseline: ActiveBaseline | null | undefined,
): BaselineDisplay | null {
  if (!baseline) return null;

  const entries = baseline.entries.filter(
    (entry): entry is typeof entry & { taskId: string } => entry.taskId !== null,
  );

  const planned = new Map(
    entries.map((entry) => [entry.taskId, { start: entry.start, finish: entry.finish }]),
  );

  const variances = compareToBaseline(
    tasks.map((task) => ({
      taskId: task.id,
      start: task.earlyStart,
      finish: task.earlyFinish,
      durationDays: task.durationDays,
    })),
    entries.map((entry) => ({
      taskId: entry.taskId,
      start: entry.start,
      finish: entry.finish,
      durationDays: entry.durationDays,
    })),
    calendarOf(plan),
  );

  return { name: baseline.name, planned, variances };
}
